import { useEffect, useState } from "react";
import { Plus, Trash2, Pencil } from "lucide-react";
import api from "../../lib/api";
import { Card, Badge } from "../../components/ui/Card";
import Button from "../../components/ui/Button";
import Input from "../../components/ui/Input";
import { cn, formatDuration, formatPrice } from "../../lib/utils";

const EMPTY_FORM = {
  name: "",
  price: "",
  duration_minutes: "",
  buffer_minutes: "",
  category: "",
  description: "",
  booking_mode: "exact",
  flexible_window_minutes: 30,
  grace_period_minutes: 15,
  max_late_minutes: 30,
  late_fee: "",
  deposit_amount: "",
  cancellation_fee: "",
  free_cancellation_hours: 24,
  is_popular: false,
};

const BOOKING_MODES = [
  { value: "exact", label: "Exact appointment", hint: "14:00 → 14:00" },
  { value: "flexible", label: "Flexible appointment", hint: "14:00 → ±30 min" },
  { value: "queue", label: "Queue booking", hint: "Join queue — estimated wait" },
];

export default function Services() {
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState(EMPTY_FORM);
  const [showForm, setShowForm] = useState(false);
  const [saving, setSaving] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [presets, setPresets] = useState([]);
  const [error, setError] = useState(null);

  const loadPresets = () =>
    api
      .get("/owner/service-presets")
      .then(({ data }) => setPresets(data))
      .catch(() => setPresets([]));

  const load = () =>
    api
      .get("/owner/services")
      .then(({ data }) => setServices(data))
      .catch(() => setServices([]))
      .finally(() => setLoading(false));

  useEffect(() => {
    load();
  }, []);

  const startCreate = () => {
    setEditingId(null);
    setForm(EMPTY_FORM);
    setShowForm(true);
    loadPresets();
  };

  // A preset fills in everything but the price; "Other" leaves the name to type.
  const applyPreset = (preset) =>
    setForm({
      ...EMPTY_FORM,
      name: preset?.name || "",
      duration_minutes: preset?.duration_minutes ?? "",
      category: preset?.category || "",
    });

  const startEdit = (service) => {
    setEditingId(service.id);
    setForm({
      name: service.name || "",
      price: service.price ?? "",
      duration_minutes: service.duration_minutes ?? "",
      buffer_minutes: service.buffer_minutes || "",
      category: service.category || "",
      description: service.description || "",
      booking_mode: service.booking_mode || "exact",
      flexible_window_minutes: service.flexible_window_minutes ?? 30,
      grace_period_minutes: service.grace_period_minutes ?? 15,
      max_late_minutes: service.max_late_minutes ?? 30,
      late_fee: service.late_fee ?? "",
      deposit_amount: service.deposit_amount ?? "",
      cancellation_fee: service.cancellation_fee ?? "",
      free_cancellation_hours: service.free_cancellation_hours ?? 24,
      is_popular: Boolean(service.is_popular),
    });
    setShowForm(true);
  };

  const cancelForm = () => {
    setShowForm(false);
    setEditingId(null);
    setForm(EMPTY_FORM);
    setError(null);
  };

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError(null);
    // No buffer typed means none.
    const payload = { ...form, buffer_minutes: Number(form.buffer_minutes) || 0 };
    try {
      if (editingId) {
        await api.put(`/owner/services/${editingId}`, payload);
      } else {
        await api.post("/owner/services", payload);
      }
      cancelForm();
      load();
    } catch (err) {
      const errors = err?.response?.data?.errors;
      setError(
        (errors && Object.values(errors)[0]?.[0]) ||
          err?.response?.data?.message ||
          "Could not save this service. Please try again."
      );
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id) => {
    await api.delete(`/owner/services/${id}`);
    load();
  };

  const toggleActive = async (service) => {
    await api.put(`/owner/services/${service.id}`, { is_active: !service.is_active });
    load();
  };

  return (
    <div>
      <div className="mb-4 flex items-center justify-between">
        <h1 className="font-montserrat text-2xl font-bold text-slate-900">Services</h1>
        <Button size="sm" onClick={() => (showForm ? cancelForm() : startCreate())}>
          <Plus className="h-4 w-4" /> Add service
        </Button>
      </div>

      {showForm && (
        <Card className="mb-6 p-4">
          <p className="mb-3 text-sm font-semibold text-slate-900">
            {editingId ? "Edit service" : "New service"}
          </p>
          {!editingId && (
            <div className="mb-4">
              <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                Popular services — pick one and set your price
              </p>
              <div className="flex flex-wrap gap-2">
                {presets.map((p) => (
                  <button
                    type="button"
                    key={p.name}
                    onClick={() => applyPreset(p)}
                    className={cn(
                      "rounded-full border px-3 py-1.5 text-sm",
                      form.name === p.name ? "border-primary bg-primary text-white" : "border-border hover:border-primary"
                    )}
                  >
                    {p.name}
                  </button>
                ))}
                <button
                  type="button"
                  onClick={() => applyPreset(null)}
                  className="rounded-full border border-dashed border-slate-400 px-3 py-1.5 text-sm text-slate-700 hover:border-primary"
                >
                  + Other (name it yourself)
                </button>
              </div>
            </div>
          )}
          <form onSubmit={submit} className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <label className="flex items-center gap-2 text-sm text-slate-700 sm:col-span-2">
              <input
                type="checkbox"
                checked={form.is_popular}
                onChange={(e) => setForm({ ...form, is_popular: e.target.checked })}
                className="h-4 w-4 accent-primary"
              />
              Show in "Popular" at the top of my shop page
            </label>
            <Input placeholder="Service name" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
            <Input placeholder="Category (e.g. Haircuts)" value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} />
            <Input placeholder="Price" type="number" required value={form.price} onChange={(e) => setForm({ ...form, price: e.target.value })} />
            <Input placeholder="Duration (minutes)" type="number" required value={form.duration_minutes} onChange={(e) => setForm({ ...form, duration_minutes: e.target.value })} />
            <Input
              placeholder="Buffer after (minutes, optional)"
              title="Time kept free after each booking of this service — to clean up or get ready. Nobody can book it."
              type="number"
              min="0"
              max="120"
              value={form.buffer_minutes}
              onChange={(e) => setForm({ ...form, buffer_minutes: e.target.value })}
            />
            <Input
              placeholder="Description (optional)"
              className="sm:col-span-2"
              value={form.description}
              onChange={(e) => setForm({ ...form, description: e.target.value })}
            />

            <div className="sm:col-span-2">
              <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                Booking mode — customers book appointments, you manage time
              </p>
              <div className="grid grid-cols-1 gap-2 sm:grid-cols-3">
                {BOOKING_MODES.map((mode) => (
                  <button
                    type="button"
                    key={mode.value}
                    onClick={() => setForm({ ...form, booking_mode: mode.value })}
                    className={cn(
                      "rounded-xl border p-3 text-left transition",
                      form.booking_mode === mode.value
                        ? "border-primary bg-primary/5"
                        : "border-border hover:border-slate-300"
                    )}
                  >
                    <p className="text-sm font-semibold text-slate-900">{mode.label}</p>
                    <p className="text-xs text-muted-foreground">{mode.hint}</p>
                  </button>
                ))}
              </div>
            </div>

            {form.booking_mode === "flexible" && (
              <Input
                placeholder="Flexible window (± minutes)"
                type="number"
                value={form.flexible_window_minutes}
                onChange={(e) => setForm({ ...form, flexible_window_minutes: e.target.value })}
              />
            )}

            <div className="sm:col-span-2">
              <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                Late-arrival policy
              </p>
              <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
                <Input
                  placeholder="Grace period (min)"
                  type="number"
                  value={form.grace_period_minutes}
                  onChange={(e) => setForm({ ...form, grace_period_minutes: e.target.value })}
                />
                <Input
                  placeholder="Max late (min)"
                  type="number"
                  value={form.max_late_minutes}
                  onChange={(e) => setForm({ ...form, max_late_minutes: e.target.value })}
                />
                <Input
                  placeholder="Late fee"
                  type="number"
                  value={form.late_fee}
                  onChange={(e) => setForm({ ...form, late_fee: e.target.value })}
                />
                <Input
                  placeholder="Deposit"
                  type="number"
                  value={form.deposit_amount}
                  onChange={(e) => setForm({ ...form, deposit_amount: e.target.value })}
                />
              </div>
            </div>

            <div className="sm:col-span-2">
              <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                Cancellation policy
              </p>
              <div className="grid grid-cols-2 gap-3">
                <Input
                  placeholder="Cancellation fee"
                  type="number"
                  value={form.cancellation_fee}
                  onChange={(e) => setForm({ ...form, cancellation_fee: e.target.value })}
                />
                <Input
                  placeholder="Free cancellation window (hours)"
                  type="number"
                  value={form.free_cancellation_hours}
                  onChange={(e) => setForm({ ...form, free_cancellation_hours: e.target.value })}
                />
              </div>
              <p className="mt-1 text-xs text-muted-foreground">
                A client who cancels later than this, or doesn't show up, has the fee kept from what they paid online; the rest is refunded.
              </p>
            </div>

            {error && <p className="text-sm text-destructive sm:col-span-2">{error}</p>}

            <div className="flex gap-2 sm:col-span-2">
              <Button type="submit" disabled={saving}>
                {saving ? "Saving…" : editingId ? "Save changes" : "Save service"}
              </Button>
              <Button type="button" variant="outline" onClick={cancelForm}>Cancel</Button>
            </div>
          </form>
        </Card>
      )}

      {loading ? (
        <p className="text-muted-foreground">Loading…</p>
      ) : services.length === 0 ? (
        <p className="text-muted-foreground">No services yet — add your first one above.</p>
      ) : (
        <div className="divide-y divide-border rounded-2xl border border-border">
          {services.map((s) => (
            <div key={s.id} className="flex flex-wrap items-center justify-between gap-3 p-4">
              <div>
                <p className="flex items-center gap-2 font-medium text-slate-900">
                  {s.name}
                  {s.is_popular && <Badge variant="warning">Popular</Badge>}
                </p>
                <p className="text-xs text-muted-foreground">
                  {s.category && `${s.category} · `}
                  {formatDuration(s.duration_minutes)}
                  {s.booking_mode && s.booking_mode !== "exact" && ` · ${BOOKING_MODES.find((m) => m.value === s.booking_mode)?.label}`}
                </p>
              </div>
              <div className="flex flex-wrap items-center gap-3">
                <span className="font-semibold text-slate-900">{formatPrice(s.price)}</span>
                <button onClick={() => toggleActive(s)}>
                  <Badge variant={s.is_active ? "accent" : "default"}>
                    {s.is_active ? "Active" : "Hidden"}
                  </Badge>
                </button>
                <button onClick={() => startEdit(s)} className="text-muted-foreground hover:text-primary">
                  <Pencil className="h-4 w-4" />
                </button>
                <button onClick={() => remove(s.id)} className="text-muted-foreground hover:text-destructive">
                  <Trash2 className="h-4 w-4" />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
